import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractErrorCode, API, getStoredToken } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import { Code2, Loader2, Download, Copy, Check, Trash2, Eye, FileCode, FileText, File as FileIcon, Archive } from "lucide-react";
import { toast } from "sonner";
import JSZip from "jszip";

function iconForPath(path) {
  if (path.endsWith(".html")) return FileCode;
  if (path.endsWith(".css")) return FileText;
  if (path.endsWith(".js")) return FileCode;
  if (path.endsWith(".json")) return FileText;
  return FileIcon;
}

function normaliseFiles(project) {
  // Back-fill legacy single-file projects so the multi-file UI just works.
  if (project?.files?.length) return project.files;
  if (project?.html_code) {
    return [{ path: "index.html", content: project.html_code, mime: "text/html" }];
  }
  return [];
}

export default function CodeBuilder() {
  const { t } = useI18n();
  const { refresh } = useAuth();
  const [description, setDescription] = useState("");
  const [generating, setGenerating] = useState(false);
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [copied, setCopied] = useState(false);
  const [activeFilePath, setActiveFilePath] = useState(null);

  const files = useMemo(() => normaliseFiles(project), [project]);
  const activeFile = useMemo(
    () => files.find((f) => f.path === activeFilePath) || files[0] || null,
    [files, activeFilePath]
  );

  const loadProjects = async () => {
    const r = await api.get("/code/projects");
    setProjects(r.data.projects || []);
  };

  useImperativeLoad(() => { loadProjects(); }, []);

  const handleGenerate = async () => {
    if (!description.trim() || generating) return;
    setGenerating(true);
    try {
      const r = await api.post("/code/generate", {
        description: description.trim(),
        multi_file: true,
      });
      setProject(r.data.project);
      setActiveFilePath(null);
      setDescription("");
      loadProjects();
      refresh();
      toast.success(t("code.generated_success"));
    } catch (err) {
      const code = extractErrorCode(err);
      const translated = t(`code.err_${code}`);
      const msg = typeof translated === "string" && !translated.startsWith("code.") ? translated : `Error: ${code}`;
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!activeFile) return;
    navigator.clipboard.writeText(activeFile.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDownloadFile = () => {
    if (!activeFile) return;
    const blob = new Blob([activeFile.content], { type: activeFile.mime || "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = activeFile.path.split("/").pop();
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadZip = async () => {
    if (!project?.project_id) return;
    // Prefer the dedicated backend endpoint when we still have an auth token —
    // it preserves filenames and content-type properly. Fall back to client-side
    // JSZip for offline / cached projects.
    try {
      const token = getStoredToken();
      const resp = await fetch(`${API}/code/projects/${project.project_id}/zip`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${project.title.replace(/\s+/g, "-").toLowerCase()}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }
    } catch (e) { /* fall through to client-side zip */ }

    const zip = new JSZip();
    files.forEach((f) => zip.file(f.path, f.content));
    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${project.title.replace(/\s+/g, "-").toLowerCase()}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSelectProject = async (id) => {
    const r = await api.get(`/code/projects/${id}`);
    setProject(r.data);
    setActiveFilePath(null);
  };

  const handleDeleteProject = async (id, e) => {
    e?.stopPropagation();
    await api.delete(`/code/projects/${id}`);
    if (project?.project_id === id) setProject(null);
    loadProjects();
  };

  return (
    <div className="flex h-full" data-testid="code-builder-page">
      {/* Project list */}
      <aside className="flex w-64 flex-col border-r border-slate-800 bg-[#070924]">
        <div className="border-b border-slate-800 p-4">
          <h2 className="font-display text-sm font-bold uppercase tracking-[0.15em] text-slate-400">
            {t("code.projects")}
          </h2>
        </div>
        <ScrollArea className="flex-1">
          {projects.length === 0 ? (
            <p className="p-4 text-xs text-slate-500" data-testid="code-empty-projects">
              {t("code.no_projects")}
            </p>
          ) : (
            <ul className="space-y-1 p-2">
              {projects.map((p) => (
                <li
                  key={p.project_id}
                  className={`group flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    project?.project_id === p.project_id
                      ? "bg-[#9ACD32]/15 text-[#9ACD32]"
                      : "text-slate-300 hover:bg-slate-800/40"
                  }`}
                  onClick={() => handleSelectProject(p.project_id)}
                  data-testid={`code-project-${p.project_id}`}
                >
                  <Code2 className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-60" />
                  <span className="line-clamp-2 flex-1 text-[13px]">{p.title}</span>
                  <button
                    className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-400"
                    onClick={(e) => handleDeleteProject(p.project_id, e)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </aside>

      {/* Main area */}
      <div className="flex flex-1 flex-col">
        <header className="border-b border-slate-800 px-8 py-5">
          <h1 className="font-display text-2xl font-bold text-white">{t("code.title")}</h1>
          <p className="mt-1 text-sm text-slate-400">{t("code.subtitle_multi")}</p>
        </header>

        <div className="border-b border-slate-800 bg-[#0A0C1F] p-5">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("code.prompt_placeholder")}
            rows={3}
            className="resize-none border-slate-700 bg-slate-900 text-[15px]"
            disabled={generating}
            data-testid="code-description-input"
          />
          <div className="mt-3 flex justify-end">
            <Button
              onClick={handleGenerate}
              disabled={!description.trim() || generating}
              className="lime-glow rounded-full bg-[#9ACD32] px-6 font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="code-generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("code.generating")}
                </>
              ) : (
                <>
                  <Code2 className="mr-2 h-4 w-4" />
                  {t("code.generate_btn")}
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {project ? (
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-slate-800 px-6 py-3">
                <div className="min-w-0">
                  <h3 className="font-display text-base font-bold text-white">{project.title}</h3>
                  <p className="line-clamp-1 text-xs text-slate-500">{project.description}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopy}
                    disabled={!activeFile}
                    className="rounded-full border-slate-700 text-xs"
                    data-testid="code-copy-btn"
                  >
                    {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
                    {copied ? t("code.copied") : t("code.copy_file")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownloadFile}
                    disabled={!activeFile}
                    className="rounded-full border-slate-700 text-xs"
                    data-testid="code-download-file-btn"
                  >
                    <Download className="mr-1 h-3 w-3" />
                    {t("code.download_file")}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleDownloadZip}
                    className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                    data-testid="code-download-zip-btn"
                  >
                    <Archive className="mr-1 h-3 w-3" />
                    {t("code.download_zip")}
                  </Button>
                </div>
              </div>

              <Tabs defaultValue="preview" className="flex flex-1 flex-col overflow-hidden">
                <TabsList className="mx-6 mt-3 w-fit bg-slate-900">
                  <TabsTrigger value="preview" data-testid="code-tab-preview">
                    <Eye className="mr-1 h-3 w-3" />
                    {t("code.preview")}
                  </TabsTrigger>
                  <TabsTrigger value="code" data-testid="code-tab-code">
                    <Code2 className="mr-1 h-3 w-3" />
                    {t("code.code")}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="preview" className="flex-1 overflow-hidden p-6 pt-3">
                  <iframe
                    title={project.title}
                    srcDoc={project.html_code}
                    sandbox="allow-scripts allow-same-origin"
                    className="code-preview h-full w-full"
                    data-testid="code-preview-iframe"
                  />
                </TabsContent>

                <TabsContent value="code" className="flex flex-1 overflow-hidden p-0">
                  {/* File tree on the left */}
                  <aside
                    className="flex w-56 shrink-0 flex-col border-r border-slate-800 bg-[#070924]"
                    data-testid="code-file-tree"
                  >
                    <div className="border-b border-slate-800 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {t("code.files_label", { count: files.length })}
                    </div>
                    <ul className="flex-1 space-y-0.5 overflow-y-auto p-2">
                      {files.map((f) => {
                        const Icon = iconForPath(f.path);
                        const isActive = (activeFile?.path || files[0]?.path) === f.path;
                        return (
                          <li key={f.path}>
                            <button
                              type="button"
                              onClick={() => setActiveFilePath(f.path)}
                              className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-[12.5px] transition-colors ${
                                isActive
                                  ? "bg-[#9ACD32]/15 text-[#9ACD32]"
                                  : "text-slate-300 hover:bg-slate-800/40"
                              }`}
                              data-testid={`code-file-${f.path}`}
                            >
                              <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" />
                              <span className="truncate font-mono">{f.path}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </aside>

                  {/* Code viewer */}
                  <div className="flex-1 overflow-auto">
                    {activeFile ? (
                      <pre
                        className="overflow-auto p-4 font-mono text-[12.5px] leading-relaxed text-slate-200"
                        data-testid="code-output"
                      >
                        <code>{activeFile.content}</code>
                      </pre>
                    ) : (
                      <p className="p-4 text-sm text-slate-500">{t("code.no_files")}</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center" data-testid="code-empty-state">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#9ACD32]/10">
                  <Code2 className="h-8 w-8 text-[#9ACD32]" />
                </div>
                <p className="font-display text-xl font-bold text-white">{t("code.no_projects")}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
